import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { dmClient } from '../../services/dmClient';
import usePageTitle from '../../hooks/usePageTitle';
import DenialsCasesTable from './DenialsCasesTable';
import { downloadCaseDocuments } from './downloadCaseDocuments';
import './DenialsListPage.css';

const STATUS_OPTIONS = [
  { value: '',                label: 'All statuses' },
  { value: 'new',             label: 'New' },
  { value: 'processing',      label: 'Processing' },
  { value: 'pending_review',  label: 'Pending Review' },
  { value: 'appealed',        label: 'Appealed' },
  { value: 'closed',          label: 'Closed' },
];

const ARCHETYPE_OPTIONS = [
  { value: '',                       label: 'All denial types' },
  { value: 'medical_necessity',      label: 'Medical Necessity' },
  { value: 'coding_ncci_mue',        label: 'Coding (NCCI/MUE)' },
  { value: 'coding_bundling',        label: 'Coding & Bundling' },
  { value: 'cob_msp',                label: 'COB / MSP' },
  { value: 'eligibility_coverage',   label: 'Eligibility / Coverage' },
  { value: 'prior_auth',             label: 'Prior Authorization' },
  { value: 'duplicate_timely',       label: 'Duplicate / Timely' },
  { value: 'documentation',          label: 'Documentation' },
  { value: 'payment_underpayment',   label: 'Payment / Underpayment' },
  { value: 'credentialing_network',  label: 'Credentialing / Network' },
  { value: 'intake_identity',        label: 'Intake / Identity' },
  { value: 'frontend_rejection',     label: 'Front-end Rejection' },
];

const QUEUES = [
  { id: 'all',    label: 'All cases' },
  { id: 'mine',   label: 'Personal Queue' },
  { id: 'shared', label: 'Shared Queue' },
];

const formatCurrency = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (Number.isNaN(n)) return '—';
  return n.toLocaleString(undefined, {
    style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0,
  });
};

const formatPct = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (Number.isNaN(n)) return '—';
  const pct = n <= 1 ? n * 100 : n;
  return `${pct.toFixed(0)}%`;
};

function SummaryStrip({ summary }) {
  if (!summary) return null;
  const denied   = Number(summary.TotalDeniedAmount || 0);
  const expected = Number(summary.TotalExpectedRecovery || 0);
  const recovery = denied > 0 ? (expected / denied) * 100 : 0;
  const avgConf  = Number(summary.AvgConfidence || 0);
  return (
    <div className="denials-kpi-grid">
      <div className="denials-kpi denials-kpi--primary">
        <div className="denials-kpi__label">Total Cases</div>
        <div className="denials-kpi__big">{summary.TotalCases ?? 0}</div>
        <div className="denials-kpi__sub">
          {summary.AppealedCases ?? 0} appealed &middot;
          {' '}{summary.PendingReviewCases ?? 0} pending review
        </div>
      </div>
      <div className="denials-kpi denials-kpi--accent">
        <div className="denials-kpi__label">Money Denied</div>
        <div className="denials-kpi__big">{formatCurrency(denied)}</div>
        <div className="denials-kpi__sub">Sum of TotalDeniedAmount across all cases</div>
      </div>
      <div className="denials-kpi denials-kpi--success">
        <div className="denials-kpi__label">Engine&apos;s Projected Recovery</div>
        <div className="denials-kpi__big">{formatCurrency(expected)}</div>
        <div className="denials-kpi__sub">
          {recovery.toFixed(1)}% of denied &middot;
          {' '}avg confidence {formatPct(avgConf)}
        </div>
      </div>
      <div className="denials-kpi">
        <div className="denials-kpi__label">Appeals Generated</div>
        <div className="denials-kpi__big">{summary.TotalAppeals ?? 0}</div>
        <div className="denials-kpi__sub">
          {summary.AutoApprovedAppeals ?? 0} auto-approved &middot;
          {' '}{summary.HumanReviewAppeals ?? 0} need review
        </div>
      </div>
    </div>
  );
}

function DenialsListPage() {
  usePageTitle('Denials');
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('');
  const [archetype, setArchetype] = useState('');
  const [queue, setQueue] = useState('all');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [total, setTotal] = useState(0);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);

  const totalPages = useMemo(
    () => (total > 0 ? Math.ceil(total / pageSize) : 1),
    [total, pageSize]
  );

  const loadItems = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    setError(null);
    const params = {};
    if (status) params.status = status;
    if (archetype) params.archetype = archetype;
    if (queue && queue !== 'all') params.queue = queue;
    params.page = page;
    params.pageSize = pageSize;

    const fetchOnce = async () => {
      const [listResp, summaryResp] = await Promise.all([
        dmClient.get('/denials', { params }),
        dmClient.get('/denials/summary').catch(() => ({ data: { data: null } })),
      ]);
      const data = listResp.data?.data || {};
      setItems(data.items || data.cases || []);
      setTotal(data.totalCount || data.total || (data.items ? data.items.length : 0));
      setSummary(summaryResp.data?.data || null);
    };

    const maxAttempts = silent ? 1 : 6;
    let lastErr = null;
    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      try {
        await fetchOnce();
        lastErr = null;
        break;
      } catch (e) {
        lastErr = e;
        if (attempt < maxAttempts - 1) {
          const delay = Math.min(1000 * 2 ** attempt, 4000);
          await new Promise((r) => setTimeout(r, delay));
        }
      }
    }
    if (lastErr) {
      const msg = lastErr?.response?.data?.message || lastErr.message || 'Failed to load denials';
      setError(msg);
    }
    if (!silent) setLoading(false);
  }, [status, archetype, queue, page, pageSize]);

  useEffect(() => { loadItems(); }, [loadItems]);

  useEffect(() => {
    if (!autoRefresh) return undefined;
    const id = setInterval(() => { loadItems({ silent: true }); }, 15000);
    return () => clearInterval(id);
  }, [autoRefresh, loadItems]);

  const handleOpen = useCallback((caseId, { status: rowStatus, executionId } = {}) => {
    const wantsRun = !executionId
      && String(rowStatus || '').toLowerCase() === 'new';
    navigate(`/denials/${caseId}`, { state: { autostart: wantsRun } });
  }, [navigate]);

  const handleDownload = useCallback(async (caseId, { externalId } = {}) => {
    setDownloadingId(caseId);
    try {
      await downloadCaseDocuments(caseId, { externalId });
    } catch (e) {
      const msg = e?.response?.data?.message
        || (e?.response?.status === 404 ? 'No uploaded documents on this case' : null)
        || e?.message
        || 'Download failed';
      setError(msg);
    } finally {
      setDownloadingId(null);
    }
  }, []);

  return (
    <div className="denials-page">
      <div className="denials-header">
        <div>
          <h1>Denial Recovery</h1>
          <p>
            Inbound denial cases routed through the 12-component Denial
            Recovery Engine. Each row shows the live engine verdict plus the
            money it expects to recover.
          </p>
        </div>
        <div className="denials-actions">
          <label className="denials-toggle">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
            Auto-refresh
          </label>
          <button
            className="denials-btn"
            onClick={loadItems}
            disabled={loading}
          >Refresh</button>
          <button
            className="denials-btn denials-btn--primary"
            onClick={() => navigate('/denials/integrations')}
          >Data Integrations</button>
        </div>
      </div>

      <SummaryStrip summary={summary} />

      <div className="denials-queue-tabs" role="tablist" aria-label="Case queue">
        {QUEUES.map((q) => (
          <button
            type="button"
            key={q.id}
            role="tab"
            aria-selected={queue === q.id}
            className={`denials-queue-tab${queue === q.id ? ' is-active' : ''}`}
            onClick={() => { setQueue(q.id); setPage(1); }}
          >
            {q.label}
          </button>
        ))}
      </div>

      <div className="denials-filters">
        <label className="denials-field">
          <span>Status</span>
          <select
            value={status}
            onChange={(e) => { setStatus(e.target.value); setPage(1); }}
          >
            {STATUS_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </label>
        <label className="denials-field">
          <span>Denial Type</span>
          <select
            value={archetype}
            onChange={(e) => { setArchetype(e.target.value); setPage(1); }}
          >
            {ARCHETYPE_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </label>
      </div>

      {error && !loading && items.length === 0 && (
        <div className="denials-alert denials-alert--error">
          {error}
        </div>
      )}

      <DenialsCasesTable
        items={items}
        loading={loading}
        emptyMessage={
          queue === 'mine'
            ? 'No cases in your Personal Queue. Upload a denial pack from Intake to populate it.'
            : queue === 'shared'
              ? 'The Shared Queue is empty for this account.'
              : 'No denial cases yet. Upload a denial pack from Intake or wait for an integration to push 835 ERAs.'
        }
        onOpen={handleOpen}
        onDownload={handleDownload}
        downloadingId={downloadingId}
      />

      <div className="denials-pagination">
        <button
          className="denials-btn"
          disabled={page <= 1 || loading}
          onClick={() => setPage((p) => Math.max(1, p - 1))}
        >Prev</button>
        <span>Page {page} of {totalPages}</span>
        <button
          className="denials-btn"
          disabled={page >= totalPages || loading}
          onClick={() => setPage((p) => p + 1)}
        >Next</button>
      </div>
    </div>
  );
}

export default DenialsListPage;
